import * as React from "react";
import {
  ComboBox,
  IComboBox,
  IComboBoxOption,
  IComboBoxStyles,
  Label,
  Link,
  mergeStyleSets,
  SelectableOptionMenuItemType,
  Stack,
} from "@fluentui/react";
import * as Messages from "../../codegen/Messages";
import { LabelInfoTooltip, RequiredTooltip } from "../Tooltip/Tooltip";
import { BaseInputProps } from "./BaseInput";
import {
  FormContext,
  FormErrorsContext,
  FormGroupValidationModeContext,
  FormInputGroupLayout,
  FormInputGroupLayoutContext,
  FormState,
  FormValidationMode,
  FormValidationModeContext,
  getFieldErrors,
  ValidationState,
} from "./Form";
import {
  iconClass,
  labelStyles,
  labelStylesSubField,
  labelStylesSubFieldWithTooltip,
  labelStylesWithToolTip,
  linkStylesCompact,
  linkStylesWide,
  renderErrorMessages,
  renderStatusBar,
  stackItemStyles,
  stackItemStylesWithToolTip,
  subFieldBorder,
} from "./InputCommon";

// tslint:disable-next-line: interface-name
export interface ComboOption {
  /**
  * Whether or not the option is selected
  */
  selected?: boolean;
  /**
   * Id attribute associated with this option
   */
  id: string;
  /**
   * Text to render for this option
   */
  text: string;
}

export enum AutoComplete {
  ON = "on",
  OFF = "off",
}
export interface ComboProps extends BaseInputProps {
  /**
   * Label for the Combo Field
   */
  label?: string;
  /**
   * Options to be listed for selection
   */
  options: ComboOption[];
  /**
   * Value(s) to be selected intially
   */
  defaultValue?: string[];
  /**
   * Option to select multiple values
   */
  multiSelect?: boolean;
  /**
   * Option to auto complete the text search
   */
  autoComplete?: AutoComplete;
  /**
   * Select all the combo options listed
   */
  selectAllOption?: boolean;
  /**
   * Show the number of options selected
   */
  showCount?: boolean;
  /**
   * Callback to validate the selected option
   */
  validator?: (value: ComboOption[]) => string[] | undefined;
  /**
   * Callback when an option is selected or existing selection is changed
   */
  onChange?: (id: string) => void;
}

const comboBoxStyles: Partial<IComboBoxStyles> = {
  root: {
    width: "100%",
    height: "24px",
    minWidth: "20rem",
    marginTop: "5px",
  },
};

const comboBoxWideStyles = mergeStyleSets(comboBoxStyles, { root: { minWidth: "445px" } });
/**
 * The Combo component provides an option to  select a single or multiple options
 * from the list of options and provide the ability to search for a listed option
 */
export const Combo = ({
  testId,
  autoComplete,
  fieldName,
  groupName,
  label,
  options,
  disabled,
  required,
  placeholder,
  defaultValue,
  multiSelect,
  tooltip,
  inputLink,
  statusInfo,
  subField,
  selectAllOption,
  showCount,
  validator,
  onChange,
}: ComboProps): JSX.Element => {
  const { groupName: layoutGroup, layout } = React.useContext(
    FormInputGroupLayoutContext,
  );
  const comboOptions = options.map(option => ({
    key: option.id,
    text: option.text,
  } as IComboBoxOption));

  if (selectAllOption) {
    comboOptions.unshift(
      {
        key: "selectAll",
        text: Messages.actions.selectAll(),
        itemType: SelectableOptionMenuItemType.SelectAll,
      },
    );
  }
  const group = groupName || layoutGroup;
  const form: FormState = React.useContext(FormContext);
  if (!Object.keys(form).length) {
    throw new Error("Combo should be used within form");
  }
  const validationMode = React.useContext(FormValidationModeContext);
  const groupValidationMode = React.useContext(FormGroupValidationModeContext);
  const formErrors = React.useContext(FormErrorsContext);
  const fieldErrors = getFieldErrors(formErrors, fieldName, group);
  const [errorMessage, setErrorMessage] = React.useState<string[]>();
  const [selectedKeys, setSelectedKeys] = React.useState<string[]>();

  const allErrors = [...errorMessage || [], ...fieldErrors || []];

  const fieldValidation = (): void => {
    const errorMsg = validator ? validator(form.getValue(fieldName, group)) : undefined;
    const requiredMessage = required ? form.getValue(fieldName, group)?.[0]?.id === undefined
      ? [Messages.validation.requiredValidation()] : undefined : undefined;
    const errors = [...requiredMessage || [], ...errorMsg || []];
    form.setFieldState(
      errors && errors?.length > 0 ? ValidationState.INVALID : ValidationState.VALID,
      fieldName,
      group,
    );
    setErrorMessage(errors);
  };

  React.useEffect(() => {
    form.registerFieldLabel(fieldName, label);
    form.registerFieldGroup(fieldName, group);

    if (validator || required) {
      form.registerField(fieldName, group);
    }
    if (defaultValue) {
      const selectedOptions = options?.filter(option => defaultValue.includes(option.id as string));
      setSelectedKeys(defaultValue);
      form.setValue(selectedOptions, fieldName, group);
      fieldValidation();
    }
    return function cleanup() {
      if (validator || required) {
        form.unRegisterField(fieldName, group);
      }
    };
  // Only at mount time. Others props should not have side effect beyond their initial values
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let newSelection: ComboOption[] = form?.getValue(fieldName, group) !== undefined
    ? form?.getValue(fieldName, group)
    : [];

  const internalOnChange = (
    _: React.FormEvent<IComboBox>,
    option?: IComboBoxOption,
  ): void => {
    if (multiSelect && option !== undefined) {
      if (option.selected) {
        if (selectAllOption && option?.itemType && option?.itemType === SelectableOptionMenuItemType.SelectAll) {
          newSelection = (comboOptions.filter(o => o.key !== option.key)
            .map(o => ({ id: o.key as string, text: o.text as string })));
        } else {
          newSelection.push({ id: option.key as string, text: option.text });
        }
      } else if (!option.selected) {
        if (selectAllOption && option?.itemType && option?.itemType === SelectableOptionMenuItemType.SelectAll) {
          newSelection = [];
        } else {
          newSelection = newSelection.filter(item => item.id !== option.key);
        }
      }
      setSelectedKeys(newSelection.map(key => key.id));
      form.setValue(newSelection, fieldName, group);
    } else if (option !== undefined) {
      setSelectedKeys([option?.key as string]);
      newSelection = [];
      newSelection.push({ id: option?.key as string, text: option?.text as string });
      form.setValue(newSelection, fieldName, group);
    }
    if (validator || required) {
      fieldValidation();
    }
    onChange?.(option?.key.toString() || "");
  };

  React.useEffect(() => {
    if (validationMode === FormValidationMode.ON || groupValidationMode[group] === FormValidationMode.ON) {
      if (validator || required) {
        fieldValidation();
      }
    }
  // Only if validation mode changes. Others props should not have side effect beyond their initial values
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationMode, groupValidationMode]);

  const comboResourcelink = (
    <Link
      id={inputLink?.id}
      styles={
        layout === FormInputGroupLayout.COMPACT
          ? linkStylesCompact
          : linkStylesWide
      }
      onClick={() => { inputLink?.onLinkClick?.(); }}
    >
      {inputLink?.text}
    </Link>
  );

  const getShowCount = selectedKeys?.length === undefined || selectedKeys?.length === 0
    ? placeholder === undefined ? Messages.hints.showSelection("0") : placeholder
    : Messages.hints.showSelection(`${selectedKeys?.length}`);

  const comboBoxSelect = (
    <ComboBox
      id={fieldName}
      data-test-id={testId}
      options={comboOptions}
      onChange={internalOnChange}
      text={showCount ? getShowCount : undefined}
      disabled={disabled}
      placeholder={placeholder}
      multiSelect={multiSelect}
      allowFreeform
      autoComplete={autoComplete}
      defaultSelectedKey={defaultValue}
      styles={
        layout === FormInputGroupLayout.COMPACT
          ? comboBoxStyles
          : comboBoxWideStyles
      }
    />
  );

  const comboBoxFieldSeparateLabel = (
    <>
      <Stack
        horizontal={layout === FormInputGroupLayout.WIDE}
        styles={inputLink === undefined ? stackItemStyles : undefined}
      >
        <Stack
          horizontal
          styles={tooltip !== undefined ? stackItemStylesWithToolTip : undefined}
        >
          {label && (
            <Stack horizontal style={{ minWidth: "250px" }}>
              {subField && layout === FormInputGroupLayout.WIDE && (
                <span style={subFieldBorder}>{}</span>
              )}
              <Label
                htmlFor={fieldName}
                styles={
                  tooltip !== undefined
                    ? subField
                      ? labelStylesSubFieldWithTooltip
                      : labelStylesWithToolTip
                    : subField
                      ? labelStylesSubField
                      : labelStyles
                }
              >
                {label}
                {required && <RequiredTooltip />}
                {tooltip !== undefined && (
                  <LabelInfoTooltip
                    tooltip={tooltip}
                    styles={!required ? iconClass : undefined}
                  />
                )}
              </Label>
            </Stack>
          )}
        </Stack>
        {comboBoxSelect}
      </Stack>
      {inputLink !== undefined && comboResourcelink}
      {allErrors.length > 0 && renderErrorMessages(allErrors, layout)}
      {statusInfo ? renderStatusBar(statusInfo, layout) : null}
    </>
  );

  return (comboBoxFieldSeparateLabel);
};
